'use client';

import React, { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, [threshold]);

  return { ref, inView };
}

const values = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    title: 'Strategic Thinking',
    description: 'We approach every engagement with rigorous analytical frameworks and forward-looking strategic vision to help you navigate complexity.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    title: 'Business Transformation',
    description: 'We guide organizations through meaningful change — from operational restructuring to digital transformation — with minimal disruption.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Technology & Innovation',
    description: 'We leverage cutting-edge technology and innovative methodologies to create sustainable competitive advantages for our clients.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Research & Analysis',
    description: 'Our data-driven approach ensures every recommendation is grounded in thorough research, market analysis, and empirical evidence.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    title: 'Management Consulting',
    description: 'We partner with leadership teams to optimize organizational structures, improve governance, and build high-performance cultures.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    title: 'Long-Term Partnerships',
    description: 'We build enduring relationships with our clients, serving as trusted advisors who understand your business deeply over time.',
  },
];

export default function AboutSection() {
  const { ref, inView } = useInView();

  return (
    <section
      id="about"
      className="py-24"
      style={{
        background: 'linear-gradient(180deg, #E8F0FE 0%, #F0F4FF 50%, #FFFFFF 100%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-4"
            style={{
              background: 'rgba(66, 132, 245, 0.1)',
              border: '1px solid rgba(66, 132, 245, 0.2)',
              color: '#4284F5',
            }}
          >
            About Pluto7 Consulting Inc.
          </div>
          <h2
            className="font-bold text-navy-dark mb-4"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}
          >
            Professional Consulting for
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, #4284F5, #21427B)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Sustainable Business Growth
            </span>
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
            Pluto7 Consulting Inc. provides professional business consulting and management services
            designed to help organizations solve complex business challenges, improve operations, and
            identify opportunities for sustainable growth.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left: Description */}
          <div
            className={`transition-all duration-700 delay-100 ${
              inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div
              className="p-8 rounded-card"
              style={{
                background: 'white',
                boxShadow: '0 4px 24px rgba(66, 132, 245, 0.08)',
                border: '1px solid rgba(66, 132, 245, 0.1)',
              }}
            >
              <h3 className="text-xl font-bold text-navy-dark mb-4">
                Who We Are
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Based in Milpitas, California, Pluto7 Consulting Inc. is a professional services firm
                specializing in Engineering, Accounting, Research, and Management Services. We serve
                businesses across industries, providing expert guidance that drives measurable results.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our team of experienced consultants brings deep cross-industry expertise and a
                commitment to delivering tailored solutions that address your specific business
                challenges. We believe in building long-term partnerships founded on trust,
                transparency, and consistent value delivery.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  'Engineering, Accounting, Research & Management Services',
                  'Business Consulting, NEC — Milpitas, CA',
                  'Client-Focused, Results-Driven Approach',
                ]?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(66, 132, 245, 0.15)' }}
                    >
                      <svg className="w-3 h-3 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Visual */}
          <div
            className={`transition-all duration-700 delay-200 ${
              inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative">
              <div
                className="rounded-card overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #0A1526 0%, #160F46 100%)',
                  padding: '40px',
                  minHeight: '360px',
                }}
              >
                <div className="text-center mb-8">
                  <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                    style={{ background: 'rgba(66, 132, 245, 0.2)' }}
                  >
                    <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Our Mission</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    To empower organizations with the strategic insights, technological capabilities,
                    and operational excellence needed to thrive in today's dynamic business environment.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Milpitas, CA', sublabel: 'Headquarters', icon: '📍' },
                    { label: 'B2B Focus', sublabel: 'Enterprise Clients', icon: '🏢' },
                    { label: 'Multi-Industry', sublabel: 'Cross-Sector Expertise', icon: '🌐' },
                    { label: 'Full-Service', sublabel: 'End-to-End Solutions', icon: '⚡' },
                  ]?.map((item, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-xl text-center"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <div className="text-lg mb-1">{item?.icon}</div>
                      <div className="text-white text-xs font-semibold">{item?.label}</div>
                      <div className="text-gray-400 text-xs">{item?.sublabel}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Values grid */}
        <div
          className={`transition-all duration-700 delay-300 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-center text-2xl font-bold text-navy-dark mb-10">
            Our Core Principles
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values?.map((value, i) => (
              <div
                key={i}
                className="p-6 rounded-card bg-white transition-all duration-300 hover:-translate-y-1 group"
                style={{
                  boxShadow: '0 2px 16px rgba(66, 132, 245, 0.06)',
                  border: '1px solid rgba(66, 132, 245, 0.08)',
                  transitionDelay: `${i * 50}ms`,
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300 group-hover:bg-primary"
                  style={{ background: 'rgba(66, 132, 245, 0.1)', color: '#4284F5' }}
                >
                  <div className="group-hover:text-white transition-colors duration-300">
                    {value?.icon}
                  </div>
                </div>
                <h4 className="font-bold text-navy-dark mb-2">{value?.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{value?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
